// API Service - Tüm HTTP istekleri için merkezi servis
const API_BASE_URL = import.meta.env.VITE_API_URL;

class ApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  // Generic GET request
  async get(endpoint: string, options?: { responseType?: 'blob' | 'json' }) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (options?.responseType === 'blob') {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.blob();
    }

    return this.handleResponse(response);
  }

  // Generic POST request
  async post(endpoint: string, data?: any, options?: { headers?: Record<string, string> }) {
    const isFormData = data instanceof FormData;
    const headers = isFormData 
      ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      : { ...this.getAuthHeaders(), ...options?.headers };
    
    const body = isFormData ? data : (data ? JSON.stringify(data) : undefined);
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body
    });
    return this.handleResponse(response);
  }

  // Generic PUT request
  async put(endpoint: string, data?: any) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined
    });
    return this.handleResponse(response);
  }

  // Generic DELETE request
  async delete(endpoint: string) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

 private async handleResponse(response: Response) {
  if (response.status === 204) return {}; // No content, örn: DELETE sonrası
  
  if (!response.ok) {
    // Handle token expiration
    if (response.status === 401) {
      console.warn('Token expired, redirecting to login...');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Token expired');
    }
    
    // Handle rate limiting
    // TEMPORARILY DISABLED FOR DEVELOPMENT
    // if (response.status === 429) {
    //   console.warn('Rate limit reached. Please wait before making more requests.');
    //   throw new Error('Too many requests. Please wait before trying again.');
    // }
    
    let error;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      try {
        error = await response.json();
      } catch {
        error = { error: 'Network error' };
      }
    } else {
      // Handle non-JSON responses (like HTML error pages)
      const text = await response.text();
      if (text.startsWith('Too many r')) {
        error = { error: 'Rate limit exceeded' };
      } else {
        error = { error: text || 'Network error' };
      }
    }

    // Create error object with all response data
    const errorObj = new Error(error.error || `HTTP ${response.status}: ${response.statusText}`) as any;
    // Preserve additional error fields (like capex_required, limit, amount)
    Object.assign(errorObj, error);
    throw errorObj;
  }
  
  // Handle JSON response
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (jsonError) {
      console.error('Invalid JSON response:', jsonError);
      throw new Error('Invalid JSON response from server');
    }
  } else {
    // For non-JSON responses, try to parse as text
    const text = await response.text();
    if (!text) return {};
    
    try {
      return JSON.parse(text);
    } catch {
      return { data: text };
    }
  }
}

  // Auth endpoints
  async login(email: string, password: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Login API error:', error);
      throw new Error('Giriş yapılırken bir hata oluştu');
    }
  }

  async logout() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Logout API error:', error);
      throw new Error('Çıkış yapılırken bir hata oluştu');
    }
  }

  async getCurrentUser() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get current user API error:', error);
      throw new Error('Kullanıcı bilgileri alınırken hata oluştu');
    }
  }

  // Users endpoints
  async getUserById(id: number) {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getUserAssets(id: number) {
    const response = await fetch(`${API_BASE_URL}/users/${id}/assets`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async returnAllUserAssets(id: number, data: { notes?: string }) {
    const response = await fetch(`${API_BASE_URL}/users/${id}/return-all-assets`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async getUsers(roles?: string) {
    try {
      let url = `${API_BASE_URL}/users`;
      if (roles) {
        url += `?roles=${encodeURIComponent(roles)}`;
      }
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get users API error:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error instanceof Error ? error.message : String(error));
      throw error; // Re-throw original error for better debugging
    }
  }

  async getDepartments() {
    try {
      const response = await fetch(`${API_BASE_URL}/users/departments`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get departments API error:', error);
      throw new Error('Departmanlar yüklenirken hata oluştu');
    }
  }

  async exportUsers(search = '', roles = '') {
    try {
      const params = new URLSearchParams({
        ...(search && { search }),
        ...(roles && { roles })
      });
      
      const response = await fetch(`${API_BASE_URL}/users/export?${params}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Export users API error:', error);
      throw new Error('Kullanıcılar dışarı aktarılırken hata oluştu');
    }
  }

  async createUser(userData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create user API error:', error);
      throw new Error('Kullanıcı oluşturulurken hata oluştu');
    }
  }

  async updateUser(id: number, userData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update user API error:', error);
      throw new Error('Kullanıcı güncellenirken hata oluştu');
    }
  }

  async updateUserStatus(id: number, is_active: boolean) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${id}/status`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ is_active })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update user status API error:', error);
      throw new Error('Kullanıcı durumu güncellenirken hata oluştu');
    }
  }

  async updateProfile(profileData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/profile/me`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(profileData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update profile API error:', error);
      throw new Error('Profil güncellenirken hata oluştu');
    }
  }

  async deleteUser(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete user API error:', error);
      throw new Error('Kullanıcı silinirken hata oluştu');
    }
  }

  async resetUserPassword(id: number, newPassword?: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${id}/reset-password`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ newPassword })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Reset user password API error:', error);
      throw new Error('Kullanıcı parolası sıfırlanırken hata oluştu');
    }
  }

  async sendLoginReminder(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${id}/login-reminder`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Send login reminder API error:', error);
      throw new Error('Giriş bilgileri hatırlatma e-postası gönderilirken hata oluştu');
    }
  }

  // Companies endpoints
  async getCompanies() {
    try {
      const response = await fetch(`${API_BASE_URL}/companies`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get companies API error:', error);
      throw new Error('Şirketler yüklenirken hata oluştu');
    }
  }

  async createCompany(companyData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/companies`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(companyData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create company API error:', error);
      throw new Error('Şirket oluşturulurken hata oluştu');
    }
  }

  async updateCompany(id: number, companyData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/companies/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(companyData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update company API error:', error);
      throw new Error('Şirket güncellenirken hata oluştu');
    }
  }

  async deleteCompany(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/companies/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete company API error:', error);
      throw new Error('Şirket silinirken hata oluştu');
    }
  }

  // Locations endpoints
  async getLocations() {
    try {
      const response = await fetch(`${API_BASE_URL}/locations`, {
        headers: this.getAuthHeaders()
      });
      const result = await this.handleResponse(response);

      // Backend returns {success: true, data: [...]} format
      const data = result?.data || result;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('❌ [API] Get locations error:', error);
      throw new Error('Lokasyonlar yüklenirken hata oluştu');
    }
  }

  async createLocation(locationData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/locations`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(locationData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create location API error:', error);
      throw new Error('Lokasyon oluşturulurken hata oluştu');
    }
  }

  async updateLocation(id: number, locationData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/locations/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(locationData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update location API error:', error);
      throw new Error('Lokasyon güncellenirken hata oluştu');
    }
  }

  async deleteLocation(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/locations/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete location API error:', error);
      throw new Error('Lokasyon silinirken hata oluştu');
    }
  }

  // Suppliers endpoints
  async getSuppliers() {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers`, {
        headers: this.getAuthHeaders()
      });
      const result = await this.handleResponse(response);

      // Backend returns {success: true, data: [...]} format
      const data = result?.data || result;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('❌ [API] Get suppliers error:', error);
      throw new Error('Tedarikçiler yüklenirken hata oluştu');
    }
  }

  async createSupplier(supplierData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(supplierData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create supplier API error:', error);
      throw new Error('Tedarikçi oluşturulurken hata oluştu');
    }
  }

  async updateSupplier(id: number, supplierData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(supplierData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update supplier API error:', error);
      throw new Error('Tedarikçi güncellenirken hata oluştu');
    }
  }

  async deleteSupplier(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete supplier API error:', error);
      throw new Error('Tedarikçi silinirken hata oluştu');
    }
  }

  // CAPEX endpoints
  async getCapexById(id: number) {
    const response = await fetch(`${API_BASE_URL}/capex/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getCapex(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/capex${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createCapex(capexData: any) {
    const response = await fetch(`${API_BASE_URL}/capex`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(capexData)
    });
    return this.handleResponse(response);
  }

  async updateCapex(id: number, capexData: any) {
    const response = await fetch(`${API_BASE_URL}/capex/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(capexData)
    });
    return this.handleResponse(response);
  }

  async deleteCapex(id: number) {
    const response = await fetch(`${API_BASE_URL}/capex/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getCapexOrders(capexId: number) {
    const response = await fetch(`${API_BASE_URL}/capex/${capexId}/orders`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getCapexTransactions(capexId: number) {
    const response = await fetch(`${API_BASE_URL}/capex/${capexId}/transactions`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Physical Servers endpoints
  async getPhysicalServerById(id: number) {
    const response = await fetch(`${API_BASE_URL}/servers/physical/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getPhysicalServers(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/servers/physical${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createPhysicalServer(serverData: any) {
    const response = await fetch(`${API_BASE_URL}/servers/physical`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(serverData)
    });
    return this.handleResponse(response);
  }

  async updatePhysicalServer(id: number, serverData: any) {
    const response = await fetch(`${API_BASE_URL}/servers/physical/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(serverData)
    });
    return this.handleResponse(response);
  }

  async deletePhysicalServer(id: number) {
    const response = await fetch(`${API_BASE_URL}/servers/physical/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Virtual Servers endpoints
  async getVirtualServers(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/servers/virtual${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createVirtualServer(serverData: any) {
    const response = await fetch(`${API_BASE_URL}/servers/virtual`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(serverData)
    });
    return this.handleResponse(response);
  }

  async updateVirtualServer(id: number, serverData: any) {
    const response = await fetch(`${API_BASE_URL}/servers/virtual/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(serverData)
    });
    return this.handleResponse(response);
  }

  async deleteVirtualServer(id: number) {
    const response = await fetch(`${API_BASE_URL}/servers/virtual/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getVMActivities(vmId: number) {
    const response = await fetch(`${API_BASE_URL}/vm-activities/${vmId}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Server Update Checks endpoints
  async getServerUpdateChecks(serverType: string, serverId: number) {
    const response = await fetch(`${API_BASE_URL}/servers/update-checks?server_type=${serverType}&server_id=${serverId}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createServerUpdateCheck(data: any) {
    const response = await fetch(`${API_BASE_URL}/servers/update-checks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async updateServerUpdateCheck(id: number, data: any) {
    const response = await fetch(`${API_BASE_URL}/servers/update-checks/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async deleteServerUpdateCheck(id: number) {
    const response = await fetch(`${API_BASE_URL}/servers/update-checks/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async addServerUpdateItem(checkId: number, data: any) {
    const response = await fetch(`${API_BASE_URL}/servers/update-checks/${checkId}/items`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async updateServerUpdateItem(checkId: number, itemId: number, data: any) {
    const response = await fetch(`${API_BASE_URL}/servers/update-checks/${checkId}/items/${itemId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async deleteServerUpdateItem(checkId: number, itemId: number) {
    const response = await fetch(`${API_BASE_URL}/servers/update-checks/${checkId}/items/${itemId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Orders endpoints
  async getOrders(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/orders${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getOrderById(id: number) {
    const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
      headers: this.getAuthHeaders()
    });
    const result = await this.handleResponse(response);
    // Backend { success, data } wrapper'ından gerçek siparişi çıkar
    return result?.data ?? result;
  }

  async createOrder(orderData: any) {
    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData)
    });
    return this.handleResponse(response);
  }

  async updateOrder(id: number, orderData: any) {
    const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData)
    });
    return this.handleResponse(response);
  }

  async updateOrderStatus(id: number, status: string) {
    const response = await fetch(`${API_BASE_URL}/orders/${id}/status`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status })
    });
    return this.handleResponse(response);
  }

  async deleteOrder(id: number) {
    const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getItemSuggestions(search: string) {
    if (!search || search.length < 4) {
      return [];
    }
    const response = await fetch(`${API_BASE_URL}/orders/item-suggestions?search=${encodeURIComponent(search)}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getOrderActivities(orderId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/activities`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get order activities API error:', error);
      throw new Error('Sipariş aktiviteleri yüklenirken hata oluştu');
    }
  }

  async uploadOrderInvoice(orderId: number, file: File) {
    try {
      const formData = new FormData();
      formData.append('invoice', file);
      
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/upload-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Upload order invoice API error:', error);
      throw new Error('Fatura yüklenirken hata oluştu');
    }
  }

  async deleteOrderInvoice(orderId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/delete-invoice`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete order invoice API error:', error);
      throw new Error('Fatura silinirken hata oluştu');
    }
  }

  async getOrderCapexInfo(orderId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/capex-info`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get order capex info error:', error);
      return null;
    }
  }

  async approveOrderInvoice(orderId: number, invoiceAmount?: number, invoiceCurrency?: string) {
    try {
      const body: Record<string, unknown> = {};
      if (invoiceAmount != null) body.invoice_amount = invoiceAmount;
      if (invoiceCurrency) body.invoice_currency = invoiceCurrency;

      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/approve-invoice`, {
        method: 'POST',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Approve order invoice API error:', error);
      throw new Error('Fatura onaylanırken hata oluştu');
    }
  }

  // Tariffs endpoints
  async getTariffs() {
    try {
      const response = await fetch(`${API_BASE_URL}/tariffs`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get tariffs API error:', error);
      throw new Error('Tarifeler yüklenirken hata oluştu');
    }
  }

  async createTariff(tariffData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/tariffs`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(tariffData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create tariff API error:', error);
      throw new Error('Tarife oluşturulurken hata oluştu');
    }
  }

  async updateTariff(id: number, tariffData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/tariffs/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(tariffData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update tariff API error:', error);
      throw new Error('Tarife güncellenirken hata oluştu');
    }
  }

  async deleteTariff(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/tariffs/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete tariff API error:', error);
      throw new Error('Tarife silinirken hata oluştu');
    }
  }

  // Technical Services endpoints
  async getTechnicalServiceById(id: number) {
    const response = await fetch(`${API_BASE_URL}/technical-service/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getTechnicalServices(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/technical-service${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createTechnicalService(serviceData: any) {
    const response = await fetch(`${API_BASE_URL}/technical-service`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(serviceData)
    });
    return this.handleResponse(response);
  }

  async updateTechnicalService(id: number, serviceData: any) {
    const response = await fetch(`${API_BASE_URL}/technical-service/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(serviceData)
    });
    return this.handleResponse(response);
  }

  async deleteTechnicalService(id: number) {
    const response = await fetch(`${API_BASE_URL}/technical-service/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Network Switches endpoints
  async getNetworkSwitches(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/network${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getSwitchModels() {
    try {
      const response = await fetch(`${API_BASE_URL}/switch-models`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get switch models API error:', error);
      throw new Error('Switch modelleri yüklenirken hata oluştu');
    }
  }

  async createSwitchModel(modelData: any) {
    try {
      const headers = modelData instanceof FormData 
        ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        : this.getAuthHeaders();
      
      const body = modelData instanceof FormData 
        ? modelData 
        : JSON.stringify(modelData);

      const response = await fetch(`${API_BASE_URL}/switch-models`, {
        method: 'POST',
        headers,
        body
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create switch model API error:', error);
      throw new Error('Switch modeli oluşturulurken hata oluştu');
    }
  }

  async updateSwitchModel(id: number, modelData: any) {
    try {
      const headers = modelData instanceof FormData 
        ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        : this.getAuthHeaders();
      
      const body = modelData instanceof FormData 
        ? modelData 
        : JSON.stringify(modelData);

      const response = await fetch(`${API_BASE_URL}/switch-models/${id}`, {
        method: 'PUT',
        headers,
        body
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update switch model API error:', error);
      throw new Error('Switch modeli güncellenirken hata oluştu');
    }
  }

  async deleteSwitchModel(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/switch-models/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete switch model API error:', error);
      throw new Error('Switch modeli silinirken hata oluştu');
    }
  }

  // VLAN endpoints
  async getVLANs() {
    try {
      const response = await fetch(`${API_BASE_URL}/vlans`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get VLANs API error:', error);
      throw new Error('VLAN\'lar yüklenirken hata oluştu');
    }
  }

  async createVLAN(vlanData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/vlans`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(vlanData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create VLAN API error:', error);
      throw new Error('VLAN oluşturulurken hata oluştu');
    }
  }

  async updateVLAN(id: number, vlanData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/vlans/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(vlanData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update VLAN API error:', error);
      throw new Error('VLAN güncellenirken hata oluştu');
    }
  }

  async deleteVLAN(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/vlans/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete VLAN API error:', error);
      throw new Error('VLAN silinirken hata oluştu');
    }
  }

  async getSwitchVLANs(switchId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/vlans/switch/${switchId}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get switch VLANs API error:', error);
      throw new Error('Switch VLAN\'ları yüklenirken hata oluştu');
    }
  }

  // Backup endpoints
  async getSwitchBackups(switchId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/network/${switchId}/backups`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get switch backups API error:', error);
      throw new Error('Switch backup\'ları yüklenirken hata oluştu');
    }
  }

  async createSwitchBackup(switchId: number, backupData: FormData) {
    try {
      const headers = this.getAuthHeaders();
      delete headers['Content-Type']; // FormData için Content-Type otomatik ayarlanır
      
      const response = await fetch(`${API_BASE_URL}/network/${switchId}/backups`, {
        method: 'POST',
        headers: headers,
        body: backupData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create switch backup API error:', error);
      throw new Error('Backup yüklenirken hata oluştu');
    }
  }

  async deleteSwitchBackup(backupId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/network/backups/${backupId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete switch backup API error:', error);
      throw new Error('Backup silinirken hata oluştu');
    }
  }

  async analyzeNetworkSwitchConfig(configText: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/network/analyze-config`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ config_text: configText })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Analyze config API error:', error);
      throw new Error('Konfigürasyon analizi sırasında hata oluştu');
    }
  }

  async createNetworkSwitch(switchData: any) {
    const headers = switchData instanceof FormData 
      ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      : this.getAuthHeaders();
    
    const body = switchData instanceof FormData 
      ? switchData 
      : JSON.stringify(switchData);

    const response = await fetch(`${API_BASE_URL}/network`, {
      method: 'POST',
      headers,
      body
    });
    return this.handleResponse(response);
  }

  async updateNetworkSwitch(id: number, switchData: any) {
    const headers = switchData instanceof FormData 
      ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      : this.getAuthHeaders();
    
    const body = switchData instanceof FormData 
      ? switchData 
      : JSON.stringify(switchData);

    const response = await fetch(`${API_BASE_URL}/network/${id}`, {
      method: 'PUT',
      headers,
      body
    });
    return this.handleResponse(response);
  }

  async deleteNetworkSwitch(id: number) {
    const response = await fetch(`${API_BASE_URL}/network/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Network switch QR kod metodları
  async getNetworkSwitchQRCode(switchId: number, options?: { embed?: boolean; json?: boolean }) {
    try {
      const params = new URLSearchParams();
      const token = localStorage.getItem('auth_token');

      if (options?.embed) params.append('embed', 'true');
      if (options?.json) params.append('json', 'true');

      const queryString = params.toString() ? '&' + params.toString() : '';

      const response = await fetch(
        `${API_BASE_URL}/network/${switchId}/qr-code?token=${token}${queryString}`,
        { headers: this.getAuthHeaders() }
      );
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get network switch QR code API error:', error);
      throw new Error('Switch QR kodu alınırken hata oluştu');
    }
  }

  async getNetworkSwitchQRCodeHTML(switchId: number, embed: boolean = true) {
    try {
      const token = localStorage.getItem('auth_token');
      const embedParam = embed ? '?embed=true' : '';

      const response = await fetch(
        `${API_BASE_URL}/network/${switchId}/qr-code${embedParam}${token ? '&token=' + token : '?token=' + token}`,
        { headers: this.getAuthHeaders() }
      );

      if (!response.ok) {
        throw new Error('QR kod alınamadı');
      }

      return await response.text();
    } catch (error) {
      console.error('Get network switch QR code HTML API error:', error);
      throw new Error('Switch QR kodu alınırken hata oluştu');
    }
  }

  async getSwitchPorts(switchId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/network/${switchId}/ports`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get switch ports API error:', error);
      throw new Error('Switch portları yüklenirken hata oluştu');
    }
  }

  async updateSwitchPort(portId: number, portData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/network/ports/${portId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(portData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update switch port API error:', error);
      throw new Error('Switch portu güncellenirken hata oluştu');
    }
  }

  async addSwitchMaintenance(id: number, data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/network/${id}/maintenance`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Add switch maintenance API error:', error);
      throw new Error('Switch bakım kaydı eklenirken hata oluştu');
    }
  }

  // IoT Devices endpoints
  async getIoTDeviceById(id: number) {
    const response = await fetch(`${API_BASE_URL}/iot-devices/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getIoTDevices(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/iot${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createIoTDevice(deviceData: any) {
    const headers = deviceData instanceof FormData 
      ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      : this.getAuthHeaders();
    
    const body = deviceData instanceof FormData 
      ? deviceData 
      : JSON.stringify(deviceData);

    const response = await fetch(`${API_BASE_URL}/iot`, {
      method: 'POST',
      headers,
      body
    });
    return this.handleResponse(response);
  }

  async updateIoTDevice(id: number, deviceData: any) {
    const headers = deviceData instanceof FormData 
      ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      : this.getAuthHeaders();
    
    const body = deviceData instanceof FormData 
      ? deviceData 
      : JSON.stringify(deviceData);

    const response = await fetch(`${API_BASE_URL}/iot/${id}`, {
      method: 'PUT',
      headers,
      body
    });
    return this.handleResponse(response);
  }

  async deleteIoTDevice(id: number) {
    const response = await fetch(`${API_BASE_URL}/iot/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Radios endpoints
  async getRadios(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/radios${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getRadio(id: number) {
    const response = await fetch(`${API_BASE_URL}/radios/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createRadio(radioData: any) {
    const headers = radioData instanceof FormData 
      ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      : this.getAuthHeaders();
    
    const body = radioData instanceof FormData 
      ? radioData 
      : JSON.stringify(radioData);

    const response = await fetch(`${API_BASE_URL}/radios`, {
      method: 'POST',
      headers,
      body
    });
    return this.handleResponse(response);
  }

  async updateRadio(id: number, radioData: any) {
    const headers = radioData instanceof FormData 
      ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      : this.getAuthHeaders();
    
    const body = radioData instanceof FormData 
      ? radioData 
      : JSON.stringify(radioData);

    const response = await fetch(`${API_BASE_URL}/radios/${id}`, {
      method: 'PUT',
      headers,
      body
    });
    return this.handleResponse(response);
  }

  async deleteRadio(id: number) {
    const response = await fetch(`${API_BASE_URL}/radios/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async assignRadioUser(id: number, userId: number) {
    const response = await fetch(`${API_BASE_URL}/radios/${id}/assign-user`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ assigned_user_id: userId })
    });
    return this.handleResponse(response);
  }

  async unassignRadioUser(id: number) {
    const response = await fetch(`${API_BASE_URL}/radios/${id}/unassign-user`, {
      method: 'PATCH',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async generateRadioName(locationId: number) {
    const response = await fetch(`${API_BASE_URL}/radios/generate-name/${locationId}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Handheld Terminals endpoints
  async getHandheldTerminals(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/handheld-terminals${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getHandheldTerminal(id: number) {
    const response = await fetch(`${API_BASE_URL}/handheld-terminals/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createHandheldTerminal(terminalData: any) {
    const response = await fetch(`${API_BASE_URL}/handheld-terminals`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(terminalData)
    });
    return this.handleResponse(response);
  }

  async updateHandheldTerminal(id: number, terminalData: any) {
    const response = await fetch(`${API_BASE_URL}/handheld-terminals/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(terminalData)
    });
    return this.handleResponse(response);
  }

  async deleteHandheldTerminal(id: number) {
    const response = await fetch(`${API_BASE_URL}/handheld-terminals/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async checkHandheldTerminalDuplicate(type: 'ip' | 'mac', value: string, excludeId?: number) {
    const response = await fetch(`${API_BASE_URL}/handheld-terminals/check-duplicate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ type, value, exclude_id: excludeId })
    });
    return this.handleResponse(response);
  }

  async getHandheldTerminalHistory(terminalId: number) {
    const response = await fetch(`${API_BASE_URL}/handheld-terminals/${terminalId}/history`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Smart Scales endpoints
  async getSmartScales(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    try {
      const response = await fetch(`${API_BASE_URL}/scales${queryString}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get smart scales API error:', error);
      throw new Error('Akıllı teraziler yüklenirken hata oluştu');
    }
  }

  async getSmartScale(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales/${id}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get smart scale API error:', error);
      throw new Error('Akıllı terazi bilgileri yüklenirken hata oluştu');
    }
  }

  async createSmartScale(scaleData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(scaleData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create smart scale API error:', error);
      throw new Error('Akıllı terazi oluşturulurken hata oluştu');
    }
  }

  async updateSmartScale(id: number, scaleData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(scaleData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update smart scale API error:', error);
      throw new Error('Akıllı terazi güncellenirken hata oluştu');
    }
  }

  async deleteSmartScale(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete smart scale API error:', error);
      throw new Error('Akıllı terazi silinirken hata oluştu');
    }
  }

  // Scale Calibration endpoints
  async recordScaleCalibration(scaleId: number, calibrationData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales/${scaleId}/calibrate`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(calibrationData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Record scale calibration API error:', error);
      throw new Error('Kalibrasyon kaydedilirken hata oluştu');
    }
  }

  async getScaleCalibrationHistory(scaleId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales/${scaleId}/calibration-history`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get scale calibration history API error:', error);
      throw new Error('Kalibrasyon geçmişi alınırken hata oluştu');
    }
  }

  // Public Smart Scale endpoints (no authentication required)
  async getPublicSmartScale(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales/public/${id}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Get public smart scale API error:', error);
      throw new Error('Terazi bilgileri yüklenirken hata oluştu');
    }
  }

  async getPublicScaleCalibrationHistory(scaleId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/scales/public/${scaleId}/calibration-history`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Get public scale calibration history API error:', error);
      throw new Error('Kalibrasyon geçmişi alınırken hata oluştu');
    }
  }

  // Storage Units endpoints
  async getStorageUnitById(id: number) {
    const response = await fetch(`${API_BASE_URL}/storage/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getStorageUnits(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/storage${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createStorageUnit(storageData: any) {
    const response = await fetch(`${API_BASE_URL}/storage`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(storageData)
    });
    return this.handleResponse(response);
  }

  async updateStorageUnit(id: number, storageData: any) {
    const response = await fetch(`${API_BASE_URL}/storage/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(storageData)
    });
    return this.handleResponse(response);
  }

  async deleteStorageUnit(id: number) {
    const response = await fetch(`${API_BASE_URL}/storage/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getAllStoragePools() {
    const response = await fetch(`${API_BASE_URL}/storage/pools/all`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getStoragePools(storageId: number) {
    const response = await fetch(`${API_BASE_URL}/storage/${storageId}/pools`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async createStoragePool(storageId: number, poolData: any) {
    const response = await fetch(`${API_BASE_URL}/storage/${storageId}/pools`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(poolData)
    });
    return this.handleResponse(response);
  }

  async updateStoragePool(storageId: number, poolId: number, poolData: any) {
    const response = await fetch(`${API_BASE_URL}/storage/${storageId}/pools/${poolId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(poolData)
    });
    return this.handleResponse(response);
  }

  async deleteStoragePool(storageId: number, poolId: number) {
    const response = await fetch(`${API_BASE_URL}/storage/${storageId}/pools/${poolId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Notifications endpoints
  async getNotifications() {
    const response = await fetch(`${API_BASE_URL}/notifications`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async markNotificationAsRead(id: number) {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
      method: 'PUT',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Activities endpoints
  async getActivities(params?: any) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/notifications${queryString}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // System Settings endpoints

  async getSystemSetting(key: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/${key}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get system setting API error:', error);
      throw new Error('Sistem ayarı yüklenirken hata oluştu');
    }
  }

  async deleteSystemSetting(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete system setting API error:', error);
      throw new Error('Sistem ayarı silinirken hata oluştu');
    }
  }

  // Upload avatar
  async uploadAvatar(file: File) {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await fetch(`${API_BASE_URL}/upload/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Upload avatar API error:', error);
      throw new Error('Avatar yüklenirken hata oluştu');
    }
  }

  // GSM Lines
  async getGSMLineById(id: number) {
    const response = await fetch(`${API_BASE_URL}/gsm-lines/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getGSMLines() {
    try {
      const response = await fetch(`${API_BASE_URL}/gsm-lines`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get GSM lines API error:', error);
      throw new Error('GSM hatları yüklenirken hata oluştu');
    }
  }

  async createGSMLine(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/gsm-lines`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create GSM line API error:', error);
      throw new Error('GSM hattı oluşturulurken hata oluştu');
    }
  }

  async updateGSMLine(id: number, data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/gsm-lines/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update GSM line API error:', error);
      throw new Error('GSM hattı güncellenirken hata oluştu');
    }
  }

  async deleteGSMLine(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/gsm-lines/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete GSM line API error:', error);
      throw new Error('GSM hattı silinirken hata oluştu');
    }
  }

  async getGSMLineHistory(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/gsm-lines/${id}/history`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get GSM line history API error:', error);
      throw new Error('GSM hattı geçmişi yüklenirken hata oluştu');
    }
  }

  async quickAssignGSMLine(gsmLineId: number, userId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/gsm-lines/${gsmLineId}/quick-assign`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ user_id: userId })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Quick assign GSM line API error:', error);
      throw new Error('GSM hattı hızlı atama işlemi başarısız');
    }
  }

  // Damage & Theft Records
  async getDamageTheftRecordById(id: number) {
    const response = await fetch(`${API_BASE_URL}/damage-theft/${id}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getDamageTheftRecords() {
    try {
      const response = await fetch(`${API_BASE_URL}/damage-theft`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get damage theft records API error:', error);
      throw new Error('Hasar/çalınma kayıtları yüklenirken hata oluştu');
    }
  }

  async createDamageTheftRecord(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/damage-theft`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create damage theft record API error:', error);
      throw new Error('Hasar/çalınma kaydı oluşturulurken hata oluştu');
    }
  }

  async updateDamageTheftRecord(id: number, data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/damage-theft/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update damage theft record API error:', error);
      throw new Error('Hasar/çalınma kaydı güncellenirken hata oluştu');
    }
  }

  async deleteDamageTheftRecord(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/damage-theft/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete damage theft record API error:', error);
      throw new Error('Hasar/çalınma kaydı silinirken hata oluştu');
    }
  }

  // System Performance
  async getSystemPerformance() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/performance`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get system performance API error:', error);
      throw new Error('Sistem performansı yüklenirken hata oluştu');
    }
  }

  // System Settings
  async getSystemSettings() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/settings`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get system settings API error:', error);
      // Return empty array on error to prevent .find() errors
      return [];
    }
  }

  // Public System Settings (for login page)
  async getPublicSystemSettings() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/public`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await this.handleResponse(response);
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get public system settings API error:', error);
      // Return empty array on error to prevent .find() errors
      return [];
    }
  }

  async updateSystemSettings(settings: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/settings`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(settings)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update system settings API error:', error);
      throw new Error('Sistem ayarları güncellenirken hata oluştu');
    }
  }

  async createSystemSetting(settingData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/settings`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(settingData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create system setting API error:', error);
      throw new Error('Sistem ayarı oluşturulurken hata oluştu');
    }
  }

  async updateSystemSetting(id: number, settingData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/settings/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(settingData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update system setting API error:', error);
      throw new Error('Sistem ayarı güncellenirken hata oluştu');
    }
  }

  // Upsert system setting - create if not exists, update if exists
  async upsertSystemSetting(settingKey: string, settingData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/settings/upsert`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          setting_key: settingKey,
          ...settingData
        })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Upsert system setting API error:', error);
      throw new Error('Sistem ayarı kaydedilirken hata oluştu');
    }
  }

  // Logo upload
  async uploadLogo(formData: FormData) {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // FormData için Content-Type header'ını set etmiyoruz, browser otomatik ekler

      const response = await fetch(`${API_BASE_URL}/system-settings/upload-logo`, {
        method: 'POST',
        headers,
        body: formData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Upload logo API error:', error);
      throw new Error('Logo yüklenirken hata oluştu');
    }
  }

  async deleteLogo() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/delete-logo`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete logo API error:', error);
      throw new Error('Logo silinirken hata oluştu');
    }
  }

  // Favicon upload
  async uploadFavicon(formData: FormData) {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // FormData için Content-Type header'ını set etmiyoruz, browser otomatik ekler

      const response = await fetch(`${API_BASE_URL}/system-settings/upload-favicon`, {
        method: 'POST',
        headers,
        body: formData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Upload favicon API error:', error);
      throw new Error('Favicon yüklenirken hata oluştu');
    }
  }

  // Realtime
  async getOnlineUsers() {
    try {
      const response = await fetch(`${API_BASE_URL}/realtime/online-users`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get online users API error:', error);
      throw new Error('Çevrimiçi kullanıcılar yüklenirken hata oluştu');
    }
  }

  async sendHeartbeat() {
    try {
      const response = await fetch(`${API_BASE_URL}/realtime/heartbeat`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Heartbeat API error:', error);
      return false;
    }
  }

  async setOffline() {
    try {
      const response = await fetch(`${API_BASE_URL}/realtime/offline`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Set offline API error:', error);
      return false;
    }
  }

  // Messages
  async getMessages() {
    try {
      const response = await fetch(`${API_BASE_URL}/messages`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get messages API error:', error);
      throw new Error('Mesajlar yüklenirken hata oluştu');
    }
  }

  async sendMessage(receiverId: number, message: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/messages/send`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ receiver_id: receiverId, message })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Send message API error:', error);
      throw new Error('Mesaj gönderilirken hata oluştu');
    }
  }

  async markMessageAsRead(messageId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/messages/${messageId}/read`, {
        method: 'PUT',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Mark message as read API error:', error);
      throw new Error('Mesaj okundu olarak işaretlenirken hata oluştu');
    }
  }

  async markAllMessagesAsRead(senderId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/messages/mark-all-read/${senderId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Mark all messages as read API error:', error);
      throw new Error('Mesajlar okundu olarak işaretlenirken hata oluştu');
    }
  }

  async getUnreadMessageCounts() {
    try {
      const response = await fetch(`${API_BASE_URL}/messages/unread-counts`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get unread counts API error:', error);
      throw new Error('Okunmamış mesaj sayıları yüklenirken hata oluştu');
    }
  }

  // Typing indicators
  async startTyping(receiverId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/typing/start`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ receiverId })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Start typing API error:', error);
      return false;
    }
  }

  async stopTyping(receiverId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/typing/stop`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ receiverId })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Stop typing API error:', error);
      return false;
    }
  }

  async getTypingStatus(userId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/typing/status/${userId}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get typing status API error:', error);
      return { typing: false };
    }
  }

  // Cabinets
  async getCabinets() {
    try {
      const response = await fetch(`${API_BASE_URL}/cabinets`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get cabinets API error:', error);
      throw new Error('Kabinetler yüklenirken hata oluştu');
    }
  }

  async getCabinet(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/cabinets/${id}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get cabinet API error:', error);
      throw new Error('Kabin detayları yüklenirken hata oluştu');
    }
  }

  async createCabinet(data: FormData) {
    try {
      const response = await fetch(`${API_BASE_URL}/cabinets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: data
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create cabinet API error:', error);
      throw new Error('Kabin oluşturulurken hata oluştu');
    }
  }

  async updateCabinet(id: number, data: FormData) {
    try {
      const response = await fetch(`${API_BASE_URL}/cabinets/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: data
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update cabinet API error:', error);
      throw new Error('Kabin güncellenirken hata oluştu');
    }
  }

  async deleteCabinet(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/cabinets/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete cabinet API error:', error);
      throw new Error('Kabin silinirken hata oluştu');
    }
  }

  async addCabinetCleaning(id: number, data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/cabinets/${id}/cleaning`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Add cabinet cleaning API error:', error);
      throw new Error('Temizlik kaydı eklenirken hata oluştu');
    }
  }

  // Menu Permissions
  async getMenus() {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/menus`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get menus API error:', error);
      throw new Error('Menüler yüklenirken hata oluştu');
    }
  }

  async getUserPermissions(userId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/user/${userId}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get user permissions API error:', error);
      throw new Error('Kullanıcı yetkileri yüklenirken hata oluştu');
    }
  }

  async updateUserPermissions(userId: number, permissions: any[]) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/user/${userId}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ permissions })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update user permissions API error:', error);
      throw new Error('Kullanıcı yetkileri güncellenirken hata oluştu');
    }
  }

  async checkUserPermission(userId: number, menuName: string, action: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/check/${userId}/${menuName}/${action}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Check permission API error:', error);
      return { hasPermission: false };
    }
  }

  async getUserMenus(userId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/user-menus/${userId}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get user menus API error:', error);
      throw new Error('Kullanıcı menüleri yüklenirken hata oluştu');
    }
  }

  async createMenu(menuData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/menus`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(menuData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create menu API error:', error);
      throw new Error('Menü oluşturulurken hata oluştu');
    }
  }

  async updateMenu(menuId: number, menuData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/menus/${menuId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(menuData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update menu API error:', error);
      throw new Error('Menü güncellenirken hata oluştu');
    }
  }

  async deleteMenu(menuId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/menus/${menuId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete menu API error:', error);
      throw new Error('Menü silinirken hata oluştu');
    }
  }

  async getMenuPermissions(menuId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/menus/${menuId}/permissions`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get menu permissions API error:', error);
      throw new Error('Menü yetkileri yüklenirken hata oluştu');
    }
  }

  async updateMenuPermissions(menuId: number, permissions: any[]) {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-permissions/menus/${menuId}/permissions`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ permissions })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update menu permissions API error:', error);
      throw new Error('Menü yetkileri güncellenirken hata oluştu');
    }
  }

  // Activity Logging
  async logActivity(activityData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/activities`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(activityData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Log activity API error:', error);
      // Don't throw error for activity logging to prevent disrupting main flow
      return false;
    }
  }

  // Login Logs
  async getLoginLogs() {
    try {
      const response = await fetch(`${API_BASE_URL}/login-logs`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get login logs API error:', error);
      return [];
    }
  }

  async getBlockedAccounts() {
    try {
      const response = await fetch(`${API_BASE_URL}/login-logs/blocked`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get blocked accounts API error:', error);
      return [];
    }
  }

  async unblockAccount(email: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/login-logs/blocked/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Unblock account API error:', error);
      throw new Error('Hesap engeli kaldırılırken hata oluştu');
    }
  }

  async logLoginAttempt(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/login-logs/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Log login attempt API error:', error);
      return false;
    }
  }

  async clearLoginLogs() {
    try {
      const response = await fetch(`${API_BASE_URL}/login-logs/clear`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Clear login logs API error:', error);
      throw new Error('Login logları temizlenirken hata oluştu');
    }
  }

  // Role Permissions
  async getRolePermissions() {
    try {
      const response = await fetch(`${API_BASE_URL}/role-permissions`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get role permissions API error:', error);
      throw new Error('Rol yetkileri yüklenirken hata oluştu');
    }
  }

  async updateRolePermission(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/role-permissions`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update role permission API error:', error);
      throw new Error('Rol yetkisi güncellenirken hata oluştu');
    }
  }

  async getUserRolePermissions(userId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/role-permissions/user/${userId}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get user role permissions API error:', error);
      throw new Error('Kullanıcı rol yetkileri yüklenirken hata oluştu');
    }
  }

  // Backup Configurations
  async getBackupConfigs() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup-configs`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get backup configs API error:', error);
      return [];
    }
  }

  async createBackupConfig(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup-configs`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create backup config API error:', error);
      throw new Error('Yedekleme konfigürasyonu oluşturulurken hata oluştu');
    }
  }

  async updateBackupConfig(id: number, data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/configs/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update backup config API error:', error);
      throw new Error('Yedekleme konfigürasyonu güncellenirken hata oluştu');
    }
  }

  async deleteBackupConfig(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/configs/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete backup config API error:', error);
      throw new Error('Yedekleme konfigürasyonu silinirken hata oluştu');
    }
  }

  async runBackup(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/run/${id}`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Run backup API error:', error);
      throw new Error('Yedekleme başlatılırken hata oluştu');
    }
  }

  async getBackupLogs() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup-logs`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get backup logs API error:', error);
      throw new Error('Yedekleme logları yüklenirken hata oluştu');
    }
  }

  // Email Configurations
  async getEmailConfigs() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/email-configs`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get email configs API error:', error);
      return [];
    }
  }

  async createEmailConfig(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/email-configs`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create email config API error:', error);
      throw new Error('E-posta konfigürasyonu oluşturulurken hata oluştu');
    }
  }

  async updateEmailConfig(id: number, data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/email-configs/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update email config API error:', error);
      throw new Error('E-posta konfigürasyonu güncellenirken hata oluştu');
    }
  }

  async deleteEmailConfig(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/email-configs/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete email config API error:', error);
      throw new Error('E-posta konfigürasyonu silinirken hata oluştu');
    }
  }

  async testEmailConfig(id: number, testEmail: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/email-configs/${id}/test`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ test_email: testEmail })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Test email config API error:', error);
      throw new Error('E-posta testi sırasında hata oluştu');
    }
  }

  // Cron Jobs
  async getCronScripts(): Promise<{ script: string; label: string }[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron-jobs/scripts`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get cron scripts API error:', error);
      return [];
    }
  }

  async getCronJobs() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron-jobs`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get cron jobs API error:', error);
      return [];
    }
  }

  async createCronJob(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron-jobs`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create cron job API error:', error);
      throw new Error('Cron işi oluşturulurken hata oluştu');
    }
  }

  async updateCronJob(id: number, data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron-jobs/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update cron job API error:', error);
      throw new Error('Cron işi güncellenirken hata oluştu');
    }
  }

  async deleteCronJob(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron-jobs/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete cron job API error:', error);
      throw new Error('Cron işi silinirken hata oluştu');
    }
  }

  async toggleCronJob(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron-jobs/${id}/toggle`, {
        method: 'PATCH',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Toggle cron job API error:', error);
      throw new Error('Cron işi durumu değiştirilirken hata oluştu');
    }
  }

  async runCronJob(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron-jobs/${id}/run`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Run cron job API error:', error);
      throw new Error('Cron işi çalıştırılırken hata oluştu');
    }
  }

  async getCronJobLogs(jobId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron/logs/${jobId}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get cron job logs API error:', error);
      throw new Error('Cron iş logları yüklenirken hata oluştu');
    }
  }

  // System Logs
  async getSystemLogs(params?: any) {
    try {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      const response = await fetch(`${API_BASE_URL}/system-settings/system-logs${queryString}`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return { logs: Array.isArray(data?.logs) ? data.logs : Array.isArray(data) ? data : [] };
    } catch (error) {
      console.error('Get system logs API error:', error);
      return { logs: [] };
    }
  }

  async createSystemLog(data: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/system-logs`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create system log API error:', error);
      throw new Error('Sistem log kaydı oluşturulurken hata oluştu');
    }
  }

  async clearSystemLogs(olderThanDays: number = 30) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/logs?older_than_days=${olderThanDays}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Clear system logs API error:', error);
      throw new Error('Sistem logları temizlenirken hata oluştu');
    }
  }

  async getCronEligibleUsers() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/cron/eligible-users`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get eligible users API error:', error);
      throw new Error('Kullanıcılar yüklenirken hata oluştu');
    }
  }

  async getUserActivities(params?: any) {
    try {
      let queryString = '';
      if (params) {
        // undefined, null, boş string ve 'undefined' string değerleri filtrele
        const filteredParams = Object.fromEntries(
          Object.entries(params).filter(([_, value]) => 
            value !== undefined && 
            value !== null && 
            value !== '' && 
            value !== 'undefined'
          )
        );
        queryString = Object.keys(filteredParams).length > 0 ? '?' + new URLSearchParams(filteredParams).toString() : '';
      }
      const response = await fetch(`${API_BASE_URL}/user-activities${queryString}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get user activities API error:', error);
      throw new Error('Kullanıcı aktiviteleri yüklenirken hata oluştu');
    }
  }


  // Backup Schedule Management
  async getBackupSchedules() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/schedules`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get backup schedules API error:', error);
      throw new Error('Backup planları yüklenirken hata oluştu');
    }
  }

  async createBackupSchedule(scheduleData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/schedules`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(scheduleData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create backup schedule API error:', error);
      throw new Error('Backup planı oluşturulurken hata oluştu');
    }
  }

  async updateBackupSchedule(id: number, scheduleData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/schedules/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(scheduleData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update backup schedule API error:', error);
      throw new Error('Backup planı güncellenirken hata oluştu');
    }
  }

  async deleteBackupSchedule(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/schedules/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete backup schedule API error:', error);
      throw new Error('Backup planı silinirken hata oluştu');
    }
  }

  async runBackupSchedule(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/backup/schedules/${id}/run`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Run backup schedule API error:', error);
      throw new Error('Backup planı çalıştırılırken hata oluştu');
    }
  }


  // Email Configuration Test
  async testEmailConfiguration(emailConfig: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/email/test`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(emailConfig)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Test email configuration API error:', error);
      throw new Error('E-posta konfigürasyonu test edilirken hata oluştu');
    }
  }

  // Admin Users
  async getAdminUsers() {
    try {
      const response = await fetch(`${API_BASE_URL}/users?roles=admin,superadmin`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Get admin users API error:', error);
      return [];
    }
  }

  // API Keys
  async getApiKeys() {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/api-keys`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get API keys API error:', error);
      throw new Error('API anahtarları yüklenirken hata oluştu');
    }
  }

  async createApiKey(apiKeyData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/api-keys`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(apiKeyData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create API key API error:', error);
      throw new Error('API anahtarı oluşturulurken hata oluştu');
    }
  }

  async updateApiKey(id: number, apiKeyData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/api-keys/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(apiKeyData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update API key API error:', error);
      throw new Error('API anahtarı güncellenirken hata oluştu');
    }
  }

  async deleteApiKey(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/system-settings/api-keys/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete API key API error:', error);
      throw new Error('API anahtarı silinirken hata oluştu');
    }
  }

  // Exchange Rates
  async getExchangeRates() {
    try {
      const response = await fetch(`${API_BASE_URL}/exchange/rates`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get exchange rates API error:', error);
      throw new Error('Döviz kurları yüklenirken hata oluştu');
    }
  }

  async updateExchangeRates() {
    try {
      const response = await fetch(`${API_BASE_URL}/exchange/update`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update exchange rates API error:', error);
      throw new Error('Döviz kurları güncellenirken hata oluştu');
    }
  }

  // Driver Management
  async uploadDriver(formData: FormData) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/printers/drivers/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Content-Type header'ını eklemiyoruz, browser otomatik ayarlayacak
        },
        body: formData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Upload driver API error:', error);
      throw new Error('Driver yüklenirken hata oluştu');
    }
  }

  async getDrivers() {
    try {
      const response = await fetch(`${API_BASE_URL}/printers/drivers`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get drivers API error:', error);
      throw new Error('Driver\'lar yüklenirken hata oluştu');
    }
  }

  async deleteDriver(driverId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/printers/drivers/${driverId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete driver API error:', error);
      throw new Error('Driver silinirken hata oluştu');
    }
  }

  // Printer Toner Management
  async getAvailableToners(printerId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/printers/${printerId}/available-toners`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get available toners API error:', error);
      throw new Error('Toner listesi yüklenirken hata oluştu');
    }
  }

  async installToner(printerId: number, data: { consumable_id: number; quantity: number; notes?: string }) {
    try {
      const response = await fetch(`${API_BASE_URL}/printers/${printerId}/install-toner`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Install toner API error:', error);
      throw new Error('Toner takılırken hata oluştu');
    }
  }

  async getPrinterHistory(printerId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/printers/${printerId}/history`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get printer history API error:', error);
      throw new Error('Yazıcı geçmişi yüklenirken hata oluştu');
    }
  }

  // Equipment Management
  async getEquipment(params?: any) {
    try {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      const response = await fetch(`${API_BASE_URL}/equipment${queryString}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get equipment API error:', error);
      throw new Error('Ekipmanlar yüklenirken hata oluştu');
    }
  }

  async getEquipmentCategories() {
    try {
      const response = await fetch(`${API_BASE_URL}/equipment/categories`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get equipment categories API error:', error);
      throw new Error('Ekipman kategorileri yüklenirken hata oluştu');
    }
  }

  async createEquipment(equipmentData: any) {
    try {
      const headers = equipmentData instanceof FormData
        ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        : this.getAuthHeaders();

      const body = equipmentData instanceof FormData
        ? equipmentData
        : JSON.stringify(equipmentData);

      const response = await fetch(`${API_BASE_URL}/equipment`, {
        method: 'POST',
        headers,
        body
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create equipment API error:', error);
      throw new Error('Ekipman oluşturulurken hata oluştu');
    }
  }

  async updateEquipment(id: number, equipmentData: any) {
    try {
      const headers = equipmentData instanceof FormData
        ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        : this.getAuthHeaders();

      const body = equipmentData instanceof FormData
        ? equipmentData
        : JSON.stringify(equipmentData);

      const response = await fetch(`${API_BASE_URL}/equipment/${id}`, {
        method: 'PUT',
        headers,
        body
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update equipment API error:', error);
      throw new Error('Ekipman güncellenirken hata oluştu');
    }
  }

  async deleteEquipment(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/equipment/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete equipment API error:', error);
      throw new Error('Ekipman silinirken hata oluştu');
    }
  }

  // Consumables Management
  async getConsumables(params?: any) {
    try {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      const response = await fetch(`${API_BASE_URL}/consumables${queryString}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get consumables API error:', error);
      throw new Error('Sarf malzemeler yüklenirken hata oluştu');
    }
  }

  async getConsumableCategories() {
    try {
      const response = await fetch(`${API_BASE_URL}/consumables/categories`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get consumable categories API error:', error);
      throw new Error('Sarf malzeme kategorileri yüklenirken hata oluştu');
    }
  }

  async createConsumable(consumableData: any) {
    try {
      const headers = consumableData instanceof FormData
        ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        : this.getAuthHeaders();

      const body = consumableData instanceof FormData
        ? consumableData
        : JSON.stringify(consumableData);

      const response = await fetch(`${API_BASE_URL}/consumables`, {
        method: 'POST',
        headers,
        body
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create consumable API error:', error);
      throw new Error('Sarf malzeme oluşturulurken hata oluştu');
    }
  }

  async updateConsumable(id: number, consumableData: any) {
    try {
      const headers = consumableData instanceof FormData
        ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        : this.getAuthHeaders();

      const body = consumableData instanceof FormData
        ? consumableData
        : JSON.stringify(consumableData);

      const response = await fetch(`${API_BASE_URL}/consumables/${id}`, {
        method: 'PUT',
        headers,
        body
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update consumable API error:', error);
      throw new Error('Sarf malzeme güncellenirken hata oluştu');
    }
  }

  async deleteConsumable(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/consumables/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete consumable API error:', error);
      throw new Error('Sarf malzeme silinirken hata oluştu');
    }
  }

  // Circuit Management
  async getCircuits() {
    try {
      const response = await fetch(`${API_BASE_URL}/circuits`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get circuits API error:', error);
      throw new Error('Devreler yüklenirken hata oluştu');
    }
  }

  async getCircuit(circuitId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/circuits/${circuitId}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get circuit API error:', error);
      throw new Error('Devre yüklenirken hata oluştu');
    }
  }

  async createCircuit(circuitData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/circuits`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(circuitData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Create circuit API error:', error);
      throw new Error('Devre oluşturulurken hata oluştu');
    }
  }

  async updateCircuit(circuitId: number, circuitData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/circuits/${circuitId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(circuitData)
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Update circuit API error:', error);
      throw new Error('Devre güncellenirken hata oluştu');
    }
  }

  async deleteCircuit(circuitId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/circuits/${circuitId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Delete circuit API error:', error);
      throw new Error('Devre silinirken hata oluştu');
    }
  }

  async uploadCircuitContract(circuitId: number, formData: FormData) {
    try {
      const response = await fetch(`${API_BASE_URL}/circuits/${circuitId}/contract`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: formData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Upload circuit contract API error:', error);
      throw new Error('Sözleşme yüklenirken hata oluştu');
    }
  }

  async downloadCircuitContract(circuitId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/circuits/${circuitId}/contract/download`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `circuit-contract-${circuitId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download circuit contract error:', error);
      throw new Error('Sözleşme indirilirken hata oluştu');
    }
  }

  // Login Background endpoints
  async uploadLoginBackground(formData: FormData) {
    const response = await fetch(`${API_BASE_URL}/system-settings/upload-login-background`, {
      method: 'POST',
      headers: {
        'Authorization': this.getAuthHeaders().Authorization
      },
      body: formData
    });
    return this.handleResponse(response);
  }

  async deleteLoginBackground() {
    const response = await fetch(`${API_BASE_URL}/system-settings/delete-login-background`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getLoginBackground() {
    const response = await fetch(`${API_BASE_URL}/system-settings/login-background`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Radio service history method
  async getRadioServiceHistory(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/radios/${id}/service-history`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get radio service history API error:', error);
      throw new Error('Telsiz servis geçmişi alınırken hata oluştu');
    }
  }

  // Announcements methods
  async getUnreadPopupAnnouncements() {
    try {
      const response = await fetch(`${API_BASE_URL}/announcements/unread-popups`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Get unread popup announcements error:', error);
      throw error;
    }
  }

  async markAnnouncementAsRead(announcementId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/announcements/${announcementId}/mark`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'read' })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Mark announcement as read error:', error);
      throw error;
    }
  }

  async markAnnouncementAsDismissed(announcementId: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/announcements/${announcementId}/mark`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'dismiss' })
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Mark announcement as dismissed error:', error);
      throw error;
    }
  }

  // Computers methods
  async getComputers() {
    try {
      return await this.get('/computers');
    } catch (error) {
      console.error('Get computers error:', error);
      throw error;
    }
  }

  // Mobile phones methods
  async getMobilePhones() {
    try {
      return await this.get('/mobile-phones');
    } catch (error) {
      console.error('Get mobile phones error:', error);
      throw error;
    }
  }

  // Get devices for damage theft form
  async getDamageTheftDevices() {
    try {
      return await this.get('/damage-theft/devices');
    } catch (error) {
      console.error('Get damage theft devices error:', error);
      throw error;
    }
  }

  // Checklist methods
  async getChecklists() {
    return this.get('/checklists');
  }

  async getTodayChecklists() {
    return this.get('/checklists/today');
  }

  async getTodayChecklistLogs() {
    return this.get('/checklists/today-logs');
  }

  async createChecklist(data: any) {
    return this.post('/checklists', data);
  }

  async updateChecklist(id: number, data: any) {
    return this.put(`/checklists/${id}`, data);
  }

  async deleteChecklist(id: number) {
    return this.delete(`/checklists/${id}`);
  }

  async saveChecklistLog(data: any) {
    return this.post('/checklists/logs', data);
  }

  async getChecklistHistory(params: { page?: number; limit?: number; checklist_id?: number } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.checklist_id) query.set('checklist_id', String(params.checklist_id));
    return this.get(`/checklists/history?${query.toString()}`);
  }

  async getChecklistLeaderboard() {
    return this.get('/checklists/leaderboard');
  }

  async getChecklistActivity(limit = 50) {
    return this.get(`/checklists/activity?limit=${limit}`);
  }

  async getAllAdminChecklists() {
    return this.get('/checklists/all-admins');
  }

  async getMyChecklistPoints() {
    return this.get('/checklists/my-points');
  }

  async getChangelog(page = 1, limit = 50) {
    return this.get(`/changelog?page=${page}&limit=${limit}`);
  }

  // Notification Sounds
  async getSounds() {
    return this.get('/system-settings/sounds');
  }

  async uploadSound(formData: FormData) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_BASE_URL}/system-settings/sounds/upload`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData
    });
    return this.handleResponse(response);
  }

  async setActiveSound(filename: string | null) {
    const response = await fetch(`${API_BASE_URL}/system-settings/sounds/active`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ filename })
    });
    return this.handleResponse(response);
  }

  async deleteSound(filename: string) {
    const response = await fetch(`${API_BASE_URL}/system-settings/sounds/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

}

export const apiService = new ApiService();
export default apiService;